import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetMarketerByUserIdQuery,
  useGetMarketerByMarketerIdQuery,
} from "../features/marketing/marketingApiSlice";
import { useGetShopLevelRankingQuery } from "../features/levelRanking/levelRankingApiSlice";

export const useMarketingDashboard = () => {
  const navigate = useNavigate();
  const { name } = useSelector((state) => state.user);
  const authUser = useSelector((state) => state.auth?.user);
  const userId = name?._id || authUser?._id || null;

  const [isOpen, setIsOpen] = useState(false);
  const [activemodal, setActivemodal] = useState(false);
  const [show, setShow] = useState(false);
  const [visibleReviews, setVisibleReviews] = useState(3);

  // 1. Fetch marketer data by userId
  const {
    data: marketerRes,
    isLoading: isMarketerLoading,
    isError: isMarketerError,
    error: marketerError,
  } = useGetMarketerByUserIdQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  const marketerData = marketerRes?.data?.[0] || null;
  const marketerId = marketerData?.marketerId || null;

  // 2. Fetch rating/marketer review data by marketerId
  const {
    data: ratingRes,
    isLoading: isRatingLoading,
    isError: isRatingError,
    error: ratingError,
  } = useGetMarketerByMarketerIdQuery(marketerId, {
    skip: !marketerId,
  });

  // 3. Active shop & level ranking progression query
  const selectedShopId =
    (typeof window !== "undefined" && localStorage.getItem("shopid")) ||
    undefined;

  const {
    data: levelRankingRes,
    refetch: refetchLevelRanking,
  } = useGetShopLevelRankingQuery(
    {
      userId: userId || undefined,
      shopId: selectedShopId || undefined,
    },
    {
      skip: !userId,
      refetchOnMountOrArgChange: true,
    }
  );

  // 4. Real-time updates without polling, without websockets, and without page reload
  useEffect(() => {
    const handleRealtimeUpdate = () => {
      refetchLevelRanking?.();
    };

    window.addEventListener(
      "selectezi:level_ranking_update",
      handleRealtimeUpdate
    );
    window.addEventListener(
      "selectezi:connection_updated",
      handleRealtimeUpdate
    );
    window.addEventListener("shop_invitation_accepted", handleRealtimeUpdate);

    let bc = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        bc = new BroadcastChannel("selectezi_level_ranking");
        bc.onmessage = () => {
          refetchLevelRanking?.();
        };
      } catch (_) {}
    }

    return () => {
      window.removeEventListener(
        "selectezi:level_ranking_update",
        handleRealtimeUpdate
      );
      window.removeEventListener(
        "selectezi:connection_updated",
        handleRealtimeUpdate
      );
      window.removeEventListener(
        "shop_invitation_accepted",
        handleRealtimeUpdate
      );
      if (bc) {
        try {
          bc.close();
        } catch (_) {}
      }
    };
  }, [refetchLevelRanking]);

  useEffect(() => {
    if (!isMarketerLoading && !isMarketerError && marketerRes) {
      const hasNoMarketerRecord =
        !Array.isArray(marketerRes.data) || marketerRes.data.length === 0;
      if (hasNoMarketerRecord) {
        navigate("/our-marketing-partner", { replace: true });
      }
    }
  }, [isMarketerLoading, isMarketerError, marketerRes, navigate]);

  const showMoreReviews = () => setVisibleReviews((prev) => prev + 3);
  const onHide = () => setShow(false);

  // Level Progression stats
  const progression = levelRankingRes?.progression || null;
  const completedLevelsCount =
    Number(progression?.completedLevelsCount) ||
    (progression?.isLevel1Completed ? 1 : 0);
  const isLevelCompleted = completedLevelsCount >= 1;
  const completedLevel = completedLevelsCount;
  const completedTierName =
    progression?.tiers?.[completedLevelsCount - 1]?.name ||
    (completedLevelsCount === 1
      ? "Bronze Starter"
      : `Tier ${completedLevelsCount}`);
  const commissionRate =
    progression?.activeCommissionRate ??
    (5.0 + Math.max(0, completedLevelsCount - 1) * 1.0);

  // Combine loading and error states
  const isLoading = isMarketerLoading || (marketerId && isRatingLoading);
  const isError = isMarketerError || (marketerId && isRatingError);
  const error = marketerError || ratingError;

  return {
    name,
    isOpen,
    setIsOpen,
    activemodal,
    setActivemodal,
    show,
    setShow,
    visibleReviews,
    showMoreReviews,
    onHide,
    marketerdata: marketerData,
    checkboxData: marketerRes,
    projectStatus: marketerRes?.projectStats || {},
    ratingData: ratingRes,
    levelRanking: levelRankingRes,
    levelProgression: progression,
    isLevelCompleted,
    completedLevel,
    completedTierName,
    commissionRate,
    refetchLevelRanking,
    isLoading,
    isError,
    error,
  };
};

export default useMarketingDashboard;
